import {useRef, useState} from 'react'
import {useNavigate} from '@github-ui/use-navigate'
import {graphql, useFragment} from 'react-relay'
import {Button, Flash, FormControl, Textarea, TextInput} from '@primer/react'

import styles from './MilestoneCreateForm.module.css'
import {LABELS} from '../constants/labels'
import type {UserSettingsOptionConfig} from '@github-ui/issue-create/getSafeConfig'
import type {MilestoneCreateFormRepositoryQuery$key} from './__generated__/MilestoneCreateFormRepositoryQuery.graphql'

type MilestoneCreateFormProps = {
  repository: MilestoneCreateFormRepositoryQuery$key
  optionConfig?: UserSettingsOptionConfig
}

export function MilestoneCreateForm({repository}: MilestoneCreateFormProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  // todo: date type
  const [dueDate, setDueDate] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const titleInputRef = useRef<HTMLInputElement>(null)

  const navigate = useNavigate()

  const repositoryData = useFragment(
    graphql`
      fragment MilestoneCreateFormRepositoryQuery on Repository {
        nameWithOwner
        viewerCanPush
      }
    `,
    repository,
  )

  if (!repositoryData.viewerCanPush) {
    return (
      <div className={styles.formContainer}>
        <Flash variant="danger">You don&apos;t have permission to create milestones in this repository.</Flash>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    if (!title.trim()) {
      setError('Title is required')
      titleInputRef.current?.focus()
      setIsSubmitting(false)
      return
    }

    // TODO: create milstone mutation
    setIsSubmitting(false)
  }

  const handleCancel = () => {
    navigate(`/${repositoryData.nameWithOwner}/milestones`)
  }

  return (
    <div className={styles.formContainer}>
      <div>
        <h1>{LABELS.createMilestone}</h1>
        <p>
          {LABELS.createMilestoneDescription} {LABELS.learnMorePrefix}{' '}
          <a href="https://docs.github.com/en/issues/tracking-your-work-with-issues/about-issues">
            {LABELS.milestonesAndIssues}
          </a>
          .
        </p>
      </div>

      {error && (
        <Flash variant="danger" className={styles.errorFlash}>
          {error}
        </Flash>
      )}

      <form onSubmit={handleSubmit}>
        <FormControl className={styles.formControl}>
          <FormControl.Label htmlFor="milestone-title">{LABELS.title}</FormControl.Label>
          <TextInput
            id="milestone-title"
            block
            value={title}
            onChange={e => setTitle(e.target.value)}
            required
            disabled={isSubmitting}
            placeholder={LABELS.titlePlaceholder}
            aria-required="true"
            ref={titleInputRef}
          />
        </FormControl>

        <FormControl className={styles.formControl}>
          <FormControl.Label htmlFor="milestone-due-date">{LABELS.dueDate}</FormControl.Label>
          <TextInput
            id="milestone-due-date"
            type="date"
            block
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            disabled={isSubmitting}
            placeholder={LABELS.datePlaceholder}
          />
        </FormControl>

        <FormControl className={styles.formControl}>
          <FormControl.Label htmlFor="milestone-description">{LABELS.description} (Optional)</FormControl.Label>
          <Textarea
            id="milestone-description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            disabled={isSubmitting}
            rows={5}
            placeholder={LABELS.descriptionPlaceholder}
            block
          />
        </FormControl>

        <div className={styles.buttonGroup}>
          <Button onClick={handleCancel} disabled={isSubmitting}>
            {LABELS.cancel}
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {LABELS.createMilestone}
          </Button>
        </div>
      </form>
    </div>
  )
}

try{ MilestoneCreateForm.displayName ||= 'MilestoneCreateForm' } catch {}